import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../../service/IdApi";
import PageLoadError from "../../../../components/PageLoadError";
import PageHeading from "../../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { useAppContext } from "../../../../context/appContext";
import DataUtils from "../../../../utils/DataUtils";
import { InputSwitch } from "primereact/inputswitch";

const filterOptions = [
    { name: 'Name', code: 'name' }
];

const SuggestedOffers = () => {
    const { displayAlert } = useAppContext();
    const [offers, setOffers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [offerTableState, setOfferTableState] = useState(() => {
        return {
            first: 0,
            rows: 25,
            page: 1,
            sortField: "n",
            sortOrder: 1
        };
    });
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [filterByFields, setFilterByFields] = useState([]);
    const [nameFilter, setNameFilter] = useState("");
    const [selectedOffers, setSelectedOffers] = useState([]);
    const menu = useRef(null);
    const [rowActions, setRowActions] = useState([]);
    const [reindexConfirmVisible, setReindexConfirmVisible] = useState(false);
    const [reindexAllLoading, setReindexAllLoading] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [unindexSelectedLoading, setUnindexSelectedLoading] = useState(false);
    const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);

    const { isLoading, isFetching, isError, refetch } = useQuery(["Offers", offerTableState], async () => {
        var body = {};
        body.first = offerTableState.first;
        body.rows = offerTableState.rows;
        body.sortField = offerTableState.sortField;
        body.sortOrder = offerTableState.sortOrder;

        if (filterByFields.some(filter => filter.code === "name") && nameFilter !== "") {
            body.name = nameFilter;
        }

        const result = await authApi.post("admin/offers/suggested", body);
        setOffers(result.data.offers);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
    });

    const indexOffer = async () => {
        try {
            const result = await authApi.post("admin/offers/index", { offerId: selectedOffer._id });
            displayAlert("success", "Offer Indexed", "Offer has been indexed successfully");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    }

    const unindexOffer = async () => {
        try {
            const result = await authApi.post("admin/offers/unindex", { offerId: selectedOffer._id });
            displayAlert("success", "Offer Unindexed", "Offer has been unindexed successfully");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    }

    const reindexAll = async () => {
        try {
            setReindexAllLoading(true);
            const result = await authApi.post("admin/offers/reindex-all", {});
            displayAlert("success", "Reindex Started", "All offers were reindexed");
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setReindexAllLoading(false);
        }
    }

    const updateOffer = async () => {
        if (DataUtils.isEmpty(editingOffer.n)) {
            displayAlert("error", "Name is required", "Please enter a name for the offer");
            return;
        }
        if (DataUtils.isEmpty(editingOffer.c)) {
            displayAlert("error", "Category is required", "Please enter a category for the offer");
            return;
        }
        try {
            setIsUpdateProcessing(true);
            await authApi.patch("admin/offers", editingOffer);
            displayAlert("success", "Offer Updated", "Offer has been updated successfully");
            setShowEditDialog(false);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setIsUpdateProcessing(false);
        }
    };

    const deleteOffer = async () => {
        try {
            setDeleteLoading(true);
            await authApi.delete(`admin/offers/${selectedOffer.u}`);
            displayAlert("success", "Offer Deleted", "Offer has been deleted successfully");
            setShowDeleteDialog(false);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setDeleteLoading(false);
        }
    };

    const duplicateOffer = async () => {
        try {
            var newOffer = { ...selectedOffer };
            newOffer._id = undefined;
            newOffer.n = newOffer.n + " (Copy)";
            newOffer.u = newOffer.u + "-copy";
            const result = await authApi.post("admin/offers", newOffer);
            const duplicatedOffer = result.data.offer;
            setEditingOffer(duplicatedOffer);
            setShowEditDialog(true);
            displayAlert("success", "Offer Duplicated", "You can now edit the duplicated offer");
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    };

    const toggleUploads = async (enabled) => {
        try {
            await authApi.patch(`admin/offers/${selectedOffer._id}/uploads`,
                { enabled });

            if (selectedOffer.indexed) {
                // we need to reindex the offer
                await authApi.post("admin/offers/unindex", { offerId: selectedOffer._id });
                await authApi.post("admin/offers/index", { offerId: selectedOffer._id });
            }
            displayAlert("success", `Uploads ${enabled ? "Enabled" : "Disabled"}`,
                `Uploads have been ${enabled ? "enabled"
                    : "disabled"} for this offer`);
            refetch();
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        }
    };

    const unindexSelectedOffers = async () => {
        try {
            setUnindexSelectedLoading(true);
            const offerIds = selectedOffers.map(offer => offer._id);
            await authApi.post("admin/offers/unindex-multiple", { offerIds });
            displayAlert("success", "Offers Unindexed", "Selected offers have been unindexed successfully");
            refetch();
            setSelectedOffers([]);
        } catch (error) {
            console.error(error);
            displayAlert("error", "Unexpected Error", "Please try again later");
        } finally {
            setUnindexSelectedLoading(false);
        }
    };

    function hasUploadField(offer) {
        return offer?.pricingForm?.some(f => f.type === 'uploader') || false;
    }

    useEffect(() => {
        var indexMenu =
        {
            label: "Index",
            icon: "pi pi-search-plus",
            command: () => {
                indexOffer();
            },
        }
        var unindexMenu =
        {
            label: "Unindex",
            icon: "pi pi-search-minus",
            command: () => {
                unindexOffer();
            },
        }
        var editMenu =
        {
            label: "Edit",
            icon: "pi pi-pencil",
            command: () => {
                setEditingOffer({ ...selectedOffer });
                setShowEditDialog(true);
            },
        }
        var duplicateMenu =
        {
            label: "Duplicate",
            icon: "pi pi-copy",
            command: () => {
                duplicateOffer();
            },
        }
        var viewMenu =
        {
            label: "View",
            icon: "pi pi-eye",
            command: () => {
            },
        }
        var deleteMenu =
        {
            label: "Delete",
            icon: "pi pi-trash",
            command: () => {
                setShowDeleteDialog(true);
            },
        }
        var enableUploadsMenu =
        {
            label: "Enable Uploads",
            icon: "pi pi-paperclip",
            command: () => {
                toggleUploads(true);
            },
        }
        var disableUploadsMenu =
        {
            label: "Disable Uploads",
            icon: "pi pi-ban",
            command: () => {
                toggleUploads(false);
            },
        }

        var _rowActions = [];
        if (selectedOffer?.indexed) {
            _rowActions.push(unindexMenu);
        } else {
            _rowActions.push(indexMenu);
        }
        _rowActions.push(editMenu);
        _rowActions.push(duplicateMenu);
        _rowActions.push(viewMenu);
        _rowActions.push(deleteMenu);
        if (hasUploadField(selectedOffer)) {
            _rowActions.push(disableUploadsMenu);
        } else {
            _rowActions.push(enableUploadsMenu);
        }
        setRowActions(_rowActions);
    }, [selectedOffer]);

    const pageHeader = (
        <PageHeading pageTitle="Admin - Suggested Offers">
            <div className="block">
            </div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onPage = (event) => {
        setOfferTableState(event);
    };

    const onSort = (event) => {
        setOfferTableState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const rowClicked = (e, rowData) => {
        e.preventDefault();
        setSelectedOffer(rowData);
        setShowDetailsDialog(true);
    }

    const keywordsTemplate = (rowData) => {
        return (
            <div className="">
                {rowData.keywords.map((keyword, index) => (
                    <span key={index}>{keyword}{index < rowData.keywords.length - 1 ? ', ' : ''}</span>
                ))}
            </div>
        );
    };

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const handleSearch = () => {
        refetch();
    };

    const clearFilters = () => {
        setFilterByFields([]);
        setNameFilter("");
        setTimeout(() => {
            refetch();
        }, 100);
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const showActionMenu = (e, rowData) => {
        setSelectedOffer(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const attachmentsTemplate = (offer) => {
        let attachmentsEnabled = false;
        if (offer?.pricingForm) {
            attachmentsEnabled = offer.pricingForm.some(f => f.type === 'uploader');
        }
        return attachmentsEnabled ? 'Enabled' : 'No';
    }

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row flex-wrap align-items-center">
                            <div className="font-bold mr-3 mb-2">
                                Filter By
                            </div>
                            <div className="flex mr-3 mb-2">
                                <MultiSelect value={filterByFields} onChange={(e) => setFilterByFields(e.value)} options={filterOptions} optionLabel="name"
                                    maxSelectedLabels={3} />
                            </div>
                            {filterByFields.some(filter => filter.code === "name") && (
                                <div className="flex mr-3 mb-2">
                                    <InputText
                                        value={nameFilter}
                                        onChange={(e) => setNameFilter(e.target.value)}
                                        placeholder="Name"
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            )}
                            {filterByFields.length > 0 && (
                                <>
                                    <div className="flex mb-2">
                                        <Button
                                            icon="pi pi-search"
                                            className="p-button-primary"
                                            onClick={handleSearch}
                                            tooltip="Search"
                                            loading={isLoading || isFetching}
                                            label="Search"
                                        />
                                    </div>
                                    <div className="flex mb-2">
                                        <Button onClick={clearFilters} className="p-button-text" icon="pi pi-times" tooltip="Clear Filter" />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end gap-2">
                            <Button
                                type="button"
                                icon="pi pi-search-minus"
                                onClick={unindexSelectedOffers}
                                label="Unindex Selected"
                                disabled={selectedOffers.length === 0}
                                loading={unindexSelectedLoading}
                            />
                            <Button type="button" icon="pi pi-refresh" onClick={() => setReindexConfirmVisible(true)} label="Reindex All" loading={reindexAllLoading} />
                            <Button type="button" icon="pi pi-file-export" onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <Menu model={rowActions} popup ref={menu} />
                    <DataTable
                        ref={dataTable}
                        value={offers}
                        dataKey="_id"
                        selection={selectedOffers}
                        onSelectionChange={(e) => setSelectedOffers(e.value)}
                        selectionMode="multiple"
                        paginator
                        rows={offerTableState.rows}
                        rowsPerPageOptions={[25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} suggested offers"
                        emptyMessage="No suggested offers found."
                        lazy
                        first={offerTableState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={offerTableState.sortField}
                        sortOrder={offerTableState.sortOrder}
                        loading={isLoading || isFetching || !loaded}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column field="n" header="Name" sortable />
                        <Column field="c" header="Category" sortable />
                        <Column field="s" header="Subcategory" sortable />
                        <Column field="indexed" header="Indexed" sortable body={(rowData) => rowData.indexed ? 'Yes' : 'No'} />
                        <Column field="p" header="Popular" sortable body={(rowData) => rowData.p ? 'Yes' : 'No'} />
                        <Column header="Attachments" sortable body={attachmentsTemplate} />
                        <Column field="keywords" header="Keywords" body={keywordsTemplate} sortable />
                    </DataTable>
                </div>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Offer Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedOffer && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">{selectedOffer.n}</div>
                            </div>
                        </div>
                    )}
                </Dialog>
                <Dialog
                    visible={reindexConfirmVisible}
                    style={{ width: '450px' }}
                    header="Confirm Reindex"
                    modal
                    footer={
                        <div>
                            <Button label="No" icon="pi pi-times" onClick={() => setReindexConfirmVisible(false)} className="p-button-text" />
                            <Button label="Yes" icon="pi pi-check" onClick={() => { setReindexConfirmVisible(false); reindexAll(); }} autoFocus />
                        </div>
                    }
                    onHide={() => setReindexConfirmVisible(false)}
                >
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        <span>Are you sure you want to reindex all offers?</span>
                    </div>
                </Dialog>
                <Dialog
                    visible={showEditDialog}
                    style={{ width: '450px' }}
                    header="Edit Offer"
                    modal
                    className="p-fluid"
                    footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowEditDialog(false)} />
                            <Button label="Save" icon="pi pi-check" onClick={updateOffer} loading={isUpdateProcessing} />
                        </div>
                    }
                    onHide={() => setShowEditDialog(false)}
                >
                    {editingOffer && (
                        <div>
                            <div className="field">
                                <label htmlFor="name" className="required">Name</label>
                                <InputText
                                    id="name"
                                    value={editingOffer.n}
                                    onChange={(e) => setEditingOffer(prev => ({ ...prev, n: e.target.value }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="category" className="required">Category</label>
                                <InputText
                                    id="category"
                                    value={editingOffer.c}
                                    onChange={(e) => setEditingOffer(prev => ({ ...prev, c: e.target.value }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="subcategory">Subcategory</label>
                                <InputText
                                    id="subcategory"
                                    value={editingOffer.s}
                                    onChange={(e) => setEditingOffer(prev => ({ ...prev, s: e.target.value }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="keywords">Keywords (comma-separated)</label>
                                <InputText
                                    id="keywords"
                                    value={editingOffer.keywords.join(', ')}
                                    onChange={(e) => setEditingOffer(prev => ({
                                        ...prev,
                                        keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                                    }))}
                                />
                            </div>
                            <div className="field">
                                <label htmlFor="popular">Popular</label>
                                <div className="text-gray-700 mb-2">Indicates that this service is popular in the category and would be defaulted to true when selecting a category and adding multiple services</div>
                                <div>
                                    <InputSwitch
                                        id="popular"
                                        checked={editingOffer.p}
                                        onChange={(e) => setEditingOffer(prev => ({ ...prev, p: e.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Dialog>
                <Dialog
                    visible={showDeleteDialog}
                    style={{ width: '450px' }}
                    header="Confirm Delete"
                    modal
                    footer={
                        <div>
                            <Button label="No" icon="pi pi-times" onClick={() => setShowDeleteDialog(false)} className="p-button-text" />
                            <Button label="Yes" icon="pi pi-check" onClick={deleteOffer} autoFocus loading={deleteLoading} />
                        </div>
                    }
                    onHide={() => setShowDeleteDialog(false)}
                >
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        <span>Are you sure you want to delete this offer?</span>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default SuggestedOffers;
